/**
 * ScanCapture — camera QR/barcode scanner modal for the Assistant chat.
 *
 * Decodes with the native BarcodeDetector API when the browser has it
 * (Chrome/Edge/Android — QR + common 1D retail formats); falls back to the
 * pure-JS `jsqr` decoder (QR only, dynamically imported) elsewhere (Safari).
 * A manual entry row doubles as the hardware-scanner (keyboard-wedge) path:
 * wedge scanners "type" the code and send Enter, which submits it. A wedge
 * scanner is a keyboard — the browser cannot "detect" it — so the row is
 * focused on open (desktop) and keys typed while nothing editable holds the
 * focus are steered into it.
 *
 * The camera needs a SECURE page (https:// or localhost): on plain http://
 * the browser hides `navigator.mediaDevices`, and the error says so.
 *
 * ONE copy, shared by the Assistant chat (platform + runtime client) and the
 * line entry quick-add / handheld count screens.
 *
 * `continuous` (opt-in — a wizard scan loop): the camera stays open after a
 * read, a repeat of the same code within `repeatMs` is ignored (a label held
 * in view is one scan), and what `onScan` resolves with is flashed over the
 * picture — `true` / `{ok: true}` a green OK, `false` / `{ok: false, message}`
 * a red error — before the next label. Without it nothing changes: one read,
 * then the camera closes.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { createRepeatFilter } from './repeatFilter'

/** What a continuous scan's handler may answer (flashed over the camera). */
export type ScanResult = boolean | { ok: boolean; message?: string } | void

// Minimal typing for the native BarcodeDetector (not yet in TS lib.dom).
interface DetectedBarcode { rawValue: string }
interface BarcodeDetectorLike {
  detect(source: CanvasImageSource): Promise<DetectedBarcode[]>
}
interface BarcodeDetectorCtor {
  new (opts?: { formats?: string[] }): BarcodeDetectorLike
  getSupportedFormats(): Promise<string[]>
}

const DETECT_INTERVAL_MS = 220

/** Why the camera could not start, in words the user can act on. */
function cameraErrorText(err: unknown): string {
  const tail = ' Type the code below, or use a connected scanner.'
  if (typeof window !== 'undefined' && (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia)) {
    return 'The camera needs a secure (https://) address — this page was opened over http://, so the browser blocks it.' + tail
  }
  const name = (err as { name?: string } | null)?.name
  if (name === 'NotAllowedError' || name === 'SecurityError') {
    return 'Camera permission was refused — allow the camera for this site in the browser (and, on a Mac, in System Settings → Privacy & Security → Camera).' + tail
  }
  if (name === 'NotFoundError' || name === 'OverconstrainedError') return 'No camera was found on this device.' + tail
  if (name === 'NotReadableError') return 'The camera is in use by another application.' + tail
  return 'Camera unavailable.' + tail
}

/** Focus the code row on open only where it will not pop a soft keyboard. */
const desktopPointer = () => typeof window !== 'undefined' && !!window.matchMedia?.('(pointer: fine)').matches

const isEditable = (el: Element | null) => !!el && (
  el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement
  || el instanceof HTMLSelectElement || (el as HTMLElement).isContentEditable)
// Formats worth scanning in a business app (native detector only; jsQR is QR-only).
const WANTED_FORMATS = [
  'qr_code', 'code_128', 'code_39', 'ean_13', 'ean_8', 'upc_a', 'upc_e',
  'itf', 'codabar', 'data_matrix', 'aztec', 'pdf417',
]

export function ScanCapture({ onScan, onClose, continuous = false, repeatMs = 1500, title }: {
  /** One-shot: called once, then the camera closes. Continuous: called per
   *  new code; may resolve with the outcome to flash. */
  onScan: (text: string) => ScanResult | Promise<ScanResult>
  onClose: () => void
  /** Keep the camera open for the next code (see the header). */
  continuous?: boolean
  /** Continuous: ignore the same code again within this many ms. */
  repeatMs?: number
  title?: string
}) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const doneRef = useRef(false)
  const [manual, setManual] = useState('')
  const [cameraError, setCameraError] = useState<string | null>(null)
  const [starting, setStarting] = useState(true)
  const [flash, setFlash] = useState<{ ok: boolean; text: string; message?: string; id: number } | null>(null)
  const fresh = useRef(createRepeatFilter(repeatMs))
  const flashTimer = useRef<number | undefined>(undefined)
  useEffect(() => () => { if (flashTimer.current) window.clearTimeout(flashTimer.current) }, [])

  // Continuous: hand each NEW code over and flash what the caller answered.
  const deliverContinuous = useCallback((text: string, typed: boolean) => {
    const t = text.trim()
    // A typed code always counts; a camera repeat within the window does not.
    if (!t || (!typed && !fresh.current(t))) return
    const id = Date.now() + Math.random()
    void Promise.resolve()
      .then(() => onScan(t))
      .then((res) => {
        if (res === undefined) return
        const ok = typeof res === 'boolean' ? res : !!res.ok
        const message = typeof res === 'object' && res ? res.message : undefined
        setFlash({ ok, text: t, message, id })
        if (flashTimer.current) window.clearTimeout(flashTimer.current)
        flashTimer.current = window.setTimeout(() => setFlash((f) => (f && f.id === id ? null : f)), ok ? 900 : 1800)
      })
      .catch(() => {
        setFlash({ ok: false, text: t, id })
      })
  }, [onScan])

  // Deliver a decoded/typed code exactly once, tearing the camera down first.
  const deliver = useCallback((text: string, typed = false) => {
    if (continuous) { deliverContinuous(text, typed); return }
    const t = text.trim()
    if (!t || doneRef.current) return
    doneRef.current = true
    streamRef.current?.getTracks().forEach((tr) => tr.stop())
    streamRef.current = null
    void onScan(t)
  }, [onScan, continuous, deliverContinuous])

  // The camera loop reads the LATEST handler: a parent re-rendering (a
  // continuous scan updates its screen after every read) must not restart
  // the camera.
  const deliverRef = useRef(deliver)
  deliverRef.current = deliver

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { onClose(); return }
      // A wedge scanner types wherever the focus is: when that is nowhere
      // editable, steer its characters into the code row.
      const input = inputRef.current
      if (!input || document.activeElement === input || isEditable(document.activeElement)) return
      if (e.ctrlKey || e.metaKey || e.altKey || e.key.length !== 1) return
      e.preventDefault()
      input.focus()
      setManual((m) => m + e.key)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  // Ready for a wedge scanner from the start on a desktop; everywhere once
  // the camera has failed.
  useEffect(() => {
    if (cameraError || desktopPointer()) inputRef.current?.focus()
  }, [cameraError])

  useEffect(() => {
    let cancelled = false
    let timer: number | undefined
    let detector: BarcodeDetectorLike | null = null
    let jsqr: ((data: Uint8ClampedArray, w: number, h: number) => { data: string } | null) | null = null

    const tick = async () => {
      const video = videoRef.current
      if (cancelled || doneRef.current || !video || video.readyState < 2) return
      try {
        if (detector) {
          const found = await detector.detect(video)
          const raw = found?.[0]?.rawValue
          if (raw) { deliverRef.current(raw); return }
        } else if (jsqr) {
          if (!canvasRef.current) canvasRef.current = document.createElement('canvas')
          const canvas = canvasRef.current
          const w = video.videoWidth, h = video.videoHeight
          if (w && h) {
            canvas.width = w; canvas.height = h
            const g = canvas.getContext('2d', { willReadFrequently: true })
            if (g) {
              g.drawImage(video, 0, 0, w, h)
              const img = g.getImageData(0, 0, w, h)
              const hit = jsqr(img.data, w, h)
              if (hit?.data) { deliverRef.current(hit.data); return }
            }
          }
        }
      } catch { /* transient decode errors — keep scanning */ }
    }

    const start = async () => {
      try {
        const BD = (window as unknown as { BarcodeDetector?: BarcodeDetectorCtor }).BarcodeDetector
        if (BD) {
          try {
            const supported = await BD.getSupportedFormats()
            const formats = WANTED_FORMATS.filter((f) => supported.includes(f))
            detector = new BD(formats.length ? { formats } : undefined)
          } catch { detector = new BD() }
        } else {
          // Safari & friends: QR-only fallback, loaded on demand.
          try {
            const mod = await import('jsqr')
            const fn = mod.default
            jsqr = (data, w, h) => fn(data, w, h, { inversionAttempts: 'dontInvert' })
          } catch { /* decoder unavailable — manual entry still works */ }
        }
        if (!navigator.mediaDevices?.getUserMedia) throw new Error('insecure context')
        let stream: MediaStream
        try {
          stream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: 'environment' }, audio: false,
          })
        } catch (err) {
          // A laptop's only camera faces the user: retry with any camera.
          if ((err as { name?: string })?.name !== 'OverconstrainedError') throw err
          stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false })
        }
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return }
        streamRef.current = stream
        const video = videoRef.current
        if (video) {
          video.srcObject = stream
          await video.play().catch(() => undefined)
        }
        if (!detector && !jsqr) {
          setCameraError('This browser cannot decode codes from the camera — type or wedge-scan the code below.')
        }
        timer = window.setInterval(tick, DETECT_INTERVAL_MS)
      } catch (err) {
        if (!cancelled) setCameraError(cameraErrorText(err))
      } finally {
        if (!cancelled) setStarting(false)
      }
    }
    start()
    return () => {
      cancelled = true
      if (timer) window.clearInterval(timer)
      streamRef.current?.getTracks().forEach((t) => t.stop())
      streamRef.current = null
    }
  }, [])

  return createPortal(
    <div style={scOverlay} onClick={onClose}>
      <div style={scSheet} onClick={(e) => e.stopPropagation()}>
        <div style={scHdr}>
          <span style={{ fontWeight: 700, fontSize: 16 }}>{title || (continuous ? 'Scan codes' : 'Scan a code')}</span>
          {continuous ? (
            <button onClick={onClose} style={scDone} title="Stop scanning (Esc)">Done</button>
          ) : (
            <button onClick={onClose} style={scX} aria-label="Close" title="Close (Esc)">✕</button>
          )}
        </div>
        {!cameraError && (
          <div style={{ position: 'relative', background: '#000', borderRadius: 10, overflow: 'hidden' }}>
            {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
            <video ref={videoRef} playsInline muted style={{ width: '100%', maxHeight: '48vh', display: 'block', objectFit: 'cover' }} />
            <div style={scFrame} aria-hidden />
            {starting && <div style={scHint}>Starting camera…</div>}
            {!starting && <div style={scHint}>Point the camera at a QR / barcode</div>}
            {flash && (
              <div role="status" data-tw-scan-flash={flash.ok ? 'ok' : 'error'} style={{
                ...scFlash, background: flash.ok ? 'rgba(22,163,74,0.88)' : 'rgba(220,38,38,0.9)',
              }}>
                <div style={{ fontSize: 23, fontWeight: 700 }}>{flash.ok ? '✓ OK' : '✕'}</div>
                <div style={{ fontSize: 13, fontFamily: 'monospace', wordBreak: 'break-all' }}>{flash.text}</div>
                {flash.message && <div style={{ fontSize: 13 }}>{flash.message}</div>}
              </div>
            )}
          </div>
        )}
        {cameraError && flash && (
          <div role="status" style={{
            fontSize: 14, borderRadius: 8, padding: '8px 12px', color: '#fff',
            background: flash.ok ? '#16a34a' : '#dc2626',
          }}>
            {flash.ok ? 'OK' : 'Not accepted'} — <code>{flash.text}</code>{flash.message ? ` · ${flash.message}` : ''}
          </div>
        )}
        {cameraError && (
          <div style={{ fontSize: 14, color: '#92400e', background: '#fffbeb', borderRadius: 8, padding: '10px 12px' }}>
            {cameraError}
          </div>
        )}
        <form onSubmit={(e) => {
          e.preventDefault()
          deliver(manual, true)
          if (continuous) setManual('')
        }} style={{ display: 'flex', gap: 8 }}>
          <input
            ref={inputRef}
            value={manual} onChange={(e) => setManual(e.currentTarget.value)}
            placeholder="…or scan with a connected scanner / type the code"
            style={{ flex: 1, padding: '9px 12px', fontSize: 15, border: '1px solid #d1d5db', borderRadius: 8, outline: 'none' }}
          />
          <button type="submit" disabled={!manual.trim()} style={{
            padding: '9px 14px', fontSize: 15, fontWeight: 600, borderRadius: 8, border: 'none',
            background: '#4f46e5', color: '#fff', cursor: 'pointer', opacity: manual.trim() ? 1 : 0.6,
          }}>Use code</button>
        </form>
      </div>
    </div>,
    document.body,
  )
}

const scOverlay: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 9800,
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
}
const scSheet: React.CSSProperties = {
  width: 'min(440px, 96vw)', background: '#fff', color: '#1f2937', borderRadius: 14,
  padding: 14, display: 'flex', flexDirection: 'column', gap: 10,
  boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
}
const scHdr: React.CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between' }
const scX: React.CSSProperties = {
  border: 'none', background: 'transparent', fontSize: 17, cursor: 'pointer', color: '#6b7280', padding: 4,
}
const scFrame: React.CSSProperties = {
  position: 'absolute', inset: '12% 18%', border: '2px solid rgba(255,255,255,0.85)',
  borderRadius: 12, boxShadow: '0 0 0 9999px rgba(0,0,0,0.25)', pointerEvents: 'none',
}
const scDone: React.CSSProperties = {
  border: 'none', borderRadius: 8, padding: '6px 14px', fontSize: 15, fontWeight: 600,
  background: '#4f46e5', color: '#fff', cursor: 'pointer',
}
const scFlash: React.CSSProperties = {
  position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)',
  minWidth: '60%', maxWidth: '90%', padding: '10px 14px', borderRadius: 12, color: '#fff',
  textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 4, pointerEvents: 'none',
}
const scHint: React.CSSProperties = {
  position: 'absolute', left: 0, right: 0, bottom: 8, textAlign: 'center',
  color: '#fff', fontSize: 13, textShadow: '0 1px 2px rgba(0,0,0,0.8)', pointerEvents: 'none',
}
